// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Pure state of an extraction preview (create-doc-interactive design, "lib/previewFlow.ts").
// Every "which preview is shown" rule lives here so it can be tested without React.
import type { BdaSchema, RunStatus } from '@bda/shared';
import { ApiError } from '../../api/client';
import { referenceOf, type Reference } from '../../lib/notify';

/** The body of an extraction preview (`POST /runs/preview`); the default payload of `PreviewRequest`. */
export interface SchemaPreviewBody {
  sourceRunId: string;
  schema: BdaSchema;
}

/**
 * What one preview request asks for: the request body `T` plus `basisKey`, which identifies the selection, fields, or
 * proposal it was built from. `T` is `{ sourceRunId, schema }` for extraction previews and the set-preview body for
 * Set_Preview_Runs (blueprint-sets Req 11); the reducer never looks inside it.
 */
export type PreviewRequest<T = SchemaPreviewBody> = T & { basisKey: string };

export type PreviewError = (
  | { kind: 'validation'; messages: string[]; fieldErrors: Record<string, string> }
  | { kind: 'bda'; message: string }
  | { kind: 'throttled'; message: string }
  | { kind: 'expired' }
  | { kind: 'split'; message: string }
  | { kind: 'network' }
  | { kind: 'other'; message: string }
) & {
  /** The failed request's trace or request ID, shown with the error (absent when the API returned neither). */
  reference?: Reference;
};

export type PreviewAttempt<T = SchemaPreviewBody> =
  | { phase: 'starting'; requestId: number; request: PreviewRequest<T> }
  | { phase: 'running'; requestId: number; request: PreviewRequest<T>; runId: string; status: 'QUEUED' | 'PROCESSING' }
  | { phase: 'failed'; requestId: number; request: PreviewRequest<T>; runId: string; reason: string | null } // Req 2.4
  | { phase: 'rejected'; requestId: number; request: PreviewRequest<T>; error: PreviewError }; // Req 2.3, 2.5-2.8

export interface PreviewState<T = SchemaPreviewBody> {
  nextRequestId: number;
  /** The most recent request only (Req 3.6). */
  attempt: PreviewAttempt<T> | null;
  /** The last succeeded preview (Req 3.5). */
  shown: { runId: string; request: PreviewRequest<T> } | null;
}

export type PreviewAction<T = SchemaPreviewBody> =
  | { type: 'request'; request: PreviewRequest<T> }
  | { type: 'accepted'; requestId: number; runId: string; status: RunStatus; failureReason: string | null }
  | { type: 'rejected'; requestId: number; error: PreviewError }
  | { type: 'runStatus'; runId: string; status: RunStatus; failureReason: string | null }
  | { type: 'clear' };

export const initialPreviewState: PreviewState = { nextRequestId: 1, attempt: null, shown: null };

/** Moves the current attempt to the phase its run status implies. */
function withRunStatus<T>(
  s: PreviewState<T>,
  attempt: PreviewAttempt<T>,
  runId: string,
  status: RunStatus,
  failureReason: string | null,
): PreviewState<T> {
  const { requestId, request } = attempt;
  switch (status) {
    case 'SUCCEEDED':
      return { ...s, attempt: null, shown: { runId, request } };
    case 'FAILED':
      return { ...s, attempt: { phase: 'failed', requestId, request, runId, reason: failureReason } };
    case 'QUEUED':
    case 'PROCESSING':
      return { ...s, attempt: { phase: 'running', requestId, request, runId, status } };
    default:
      // UPLOADING never applies to a preview (no upload step); treat it as queued.
      return { ...s, attempt: { phase: 'running', requestId, request, runId, status: 'QUEUED' } };
  }
}

export function previewReducer<T = SchemaPreviewBody>(s: PreviewState<T>, a: PreviewAction<T>): PreviewState<T> {
  switch (a.type) {
    case 'request':
      return {
        ...s,
        nextRequestId: s.nextRequestId + 1,
        attempt: { phase: 'starting', requestId: s.nextRequestId, request: a.request },
      };
    case 'accepted': {
      const cur = s.attempt;
      if (!cur || cur.phase !== 'starting' || cur.requestId !== a.requestId) return s;
      return withRunStatus(s, cur, a.runId, a.status, a.failureReason);
    }
    case 'rejected': {
      const cur = s.attempt;
      if (!cur || cur.phase !== 'starting' || cur.requestId !== a.requestId) return s;
      return { ...s, attempt: { phase: 'rejected', requestId: cur.requestId, request: cur.request, error: a.error } };
    }
    case 'runStatus': {
      const cur = s.attempt;
      // Only a running attempt takes poll updates; a failed one is terminal.
      if (!cur || cur.phase !== 'running' || cur.runId !== a.runId) return s;
      return withRunStatus(s, cur, a.runId, a.status, a.failureReason);
    }
    case 'clear':
      return { nextRequestId: s.nextRequestId, attempt: null, shown: null };
  }
}

/** Derived view of a preview state (see `previewStatus`). */
export interface PreviewStatus<T = SchemaPreviewBody> {
  busy: boolean;
  stale: boolean;
  tryAgain: PreviewRequest<T> | null;
}

/** Derived view: `busy` disables the button (Req 1.8); `stale` marks the shown preview (Req 3.5, 4.7). */
export function previewStatus<T = SchemaPreviewBody>(s: PreviewState<T>, currentBasisKey: string): PreviewStatus<T> {
  return {
    busy: s.attempt?.phase === 'starting',
    stale: s.shown !== null && s.shown.request.basisKey !== currentBasisKey,
    tryAgain: s.attempt?.phase === 'failed' ? s.attempt.request : null,
  };
}

export const PREVIEW_NETWORK_TEXT = 'The preview could not be started. Check your connection and try again.';

/** Maps a failed `createPreview` call to what the panel shows (Req 2.3, 2.5-2.8), with the request's reference. */
export function mapPreviewError(e: unknown): PreviewError {
  const mapped = mapPreviewErrorKind(e);
  const reference = referenceOf(e);
  return reference ? { ...mapped, reference } : mapped;
}

function mapPreviewErrorKind(e: unknown): PreviewError {
  if (!(e instanceof ApiError)) return { kind: 'other', message: e instanceof Error ? e.message : String(e) };
  const details = e.details ?? {};
  switch (e.code) {
    case 'VALIDATION_FAILED': {
      const raw = details.fieldErrors;
      const fieldErrors: Record<string, string> = {};
      if (raw && typeof raw === 'object') {
        for (const [k, v] of Object.entries(raw as Record<string, unknown>))
          if (typeof v === 'string') fieldErrors[k] = v;
      }
      const messages = Object.values(fieldErrors);
      // Page-limit and other 400s carry no fieldErrors: show the API's message.
      return { kind: 'validation', messages: messages.length > 0 ? messages : [e.message], fieldErrors };
    }
    case 'BDA_REJECTED':
      return { kind: 'bda', message: e.message };
    case 'THROTTLED':
      return details.capacity === 'splitProjects'
        ? { kind: 'split', message: e.message }
        : { kind: 'throttled', message: e.message };
    case 'CONFLICT':
      return details.reason === 'DOCUMENT_EXPIRED' ? { kind: 'expired' } : { kind: 'other', message: e.message };
    case 'NETWORK':
    case 'TIMEOUT':
      return { kind: 'network' };
    default:
      return { kind: 'other', message: e.message };
  }
}

/**
 * 1-based line of a JSON.parse error in `text` (Req 4.6). Reads "line N" (Firefox, newer V8) or
 * "position N" (V8); returns null when the message has neither.
 */
export function jsonErrorLine(text: string, error: unknown): number | null {
  const message = error instanceof Error ? error.message : String(error);
  const line = /\bline (\d+)/i.exec(message);
  if (line) return Number(line[1]);
  const pos = /\bposition (\d+)/i.exec(message);
  if (!pos) return null;
  const at = Math.min(Number(pos[1]), text.length);
  let n = 1;
  for (let i = 0; i < at; i++) if (text.charCodeAt(i) === 10) n++;
  return n;
}
